import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { FormLabel, InputField } from '@/components/Form';
import PhoneInputField from '@/components/Form/PhoneInputField';
import { Button, Divider, Flex, Select, Space, Spin } from 'antd';
import {
  useGetClientTokenAsUserMutation,
  useGetCotactsQuery,
  useRegisterClientByAdminMutation,
  useVerifyExistingUserMutation,
} from '@/store/apis/odinAppApi';
import { displayError, displayErrorText } from '@/utils/display-message-utils';
import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReloadOutlined } from '@ant-design/icons';
import { useAppDispatch } from '@/hooks/reduxHooks';
import {
  clearClientToken,
  setClientToken,
} from '@/store/slices/auth/authSlice';
import HubspotContactSelect from './HubspotContactSelect';
import { HubspotContact } from '@/shared/types';

const createClientSchema = yup.object().shape({
  firstName: yup.string().required('First Name is required'),
  lastName: yup.string().required('Last Name is required'),
  email: yup
    .string()
    .email('Please enter a valid email')
    .required('Email is required'),
  phone: yup.string(),
});

export type CreateClientFormType = yup.InferType<typeof createClientSchema>;

type ApplicationFormType = 'purchase' | 'refinance';
interface CreateClientProps {
  applicationType: ApplicationFormType;
}
const CreateClient: FC<CreateClientProps> = ({ applicationType }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [assessmentType, setAssessmentType] =
    useState<ApplicationFormType>(applicationType);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: yupResolver(createClientSchema),
    defaultValues: {},
  });

  const [registerClientByAdmin, { isLoading: isRegisterClientLoading }] =
    useRegisterClientByAdminMutation();

  const [verifyExistingUser, { isLoading: verifyExistingUserLoading }] =
    useVerifyExistingUserMutation();

  // const {
  //   data: contactData,
  //   isLoading: contactsLoading,
  //   isFetching,
  //   refetch,
  // } = useGetCotactsQuery();
  const [getClientToken, { isLoading: getClientTokenLoading }] =
    useGetClientTokenAsUserMutation();

  const handleLoginAsUser = async (email: string) => {
    dispatch(clearClientToken());
    getClientToken({ email }) // Make sure your mutation accepts an object with `email`
      .unwrap()
      .then((response) => {
        dispatch(setClientToken({ clientToken: response.data.access_token }));
        navigate(`/application/broker/${assessmentType}/create`);
      })
      .catch((err) => {
        console.error('Token fetch error:', err);
        displayError(err);
      });
  };

  const onSubmit: SubmitHandler<CreateClientFormType> = (data) => {
    dispatch(clearClientToken());
    registerClientByAdmin(data)
      .unwrap()
      .then((res) => {
        // handleLoginAsUser(res.data.email);
        dispatch(setClientToken({ clientToken: res.data.access_token }));
        navigate(`/application/broker/${assessmentType}/create`);
      })
      .catch((err) => {
        console.log('registerClient', err);

        displayError(err);
      });
  };

  const handleEmailChange = (value: string) => {
    setSelectedContact(value);
    handleLoginAsUser(value);
  };

  const handleStartAssessment = async () => {
    if (!selectedContact) {
      displayErrorText('Please select email');
      return;
    }
    handleLoginAsUser(selectedContact);
  };

  const verifyUser = (email: string) => {
    verifyExistingUser({ email })
      .unwrap()
      .then((response) => {
        if (response.data.exists) {
          handleLoginAsUser(email);
        }
      })
      .catch((err) => {
        displayError(err);
      });
  };

  const handleHubspotContactSelect = (contact: HubspotContact) => {
    if (contact) {
      setValue('email', contact?.properties?.email ?? '');
      setValue('firstName', contact?.properties?.firstname ?? '');
      setValue('lastName', contact?.properties?.lastname ?? '');
      setValue('phone', contact?.properties?.phone ?? '');
      if (contact?.properties?.email) {
        verifyUser(contact?.properties?.email);
      }
    }
  };

  const handleAssessmentTypeChange = (value: ApplicationFormType) => {
    setAssessmentType(value);
  };

  return (
    <>
      <div className="form-control">
        <FormLabel label="Assessment For" />
        <Select
          options={[
            {
              label: 'Purchase',
              value: 'purchase',
            },
            {
              label: 'Refinance',
              value: 'refinance',
            },
          ]}
          onChange={handleAssessmentTypeChange}
          value={assessmentType}
          style={{ width: '100%' }}
        />
      </div>
      {/* <Flex gap={10}>
        <Select
          options={contactData?.data ?? []}
          onChange={handleEmailChange}
          value={selectedContact}
          style={{ width: '100%' }}
          showSearch={true}
          fieldNames={{
            value: 'email',
            label: 'email',
          }}
          loading={contactsLoading || isFetching}
          disabled={contactsLoading || isFetching}
        />
        <Button
          onClick={refetch}
          icon={<ReloadOutlined />}
          disabled={isFetching}
        />
      </Flex> */}
      {/* <Button
        type="primary"
        style={{ marginTop: '1rem' }}
        onClick={handleStartAssessment}
        loading={getClientTokenLoading}
        disabled={getClientTokenLoading}
      >
        Start Assessment
      </Button> */}

      <Divider />
      {/* <Typography.Title level={5}>Create Contact</Typography.Title> */}
      <HubspotContactSelect onSelectContact={handleHubspotContactSelect} />
      <Divider />
      {getClientTokenLoading && (
        <Space style={{ marginTop: '2rem', marginBottom: '2rem' }}>
          <Spin tip="Starting Assessment">
            <div style={{ width: '150px' }}></div>
          </Spin>
        </Space>
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <InputField
          size="large"
          name="email"
          label="Email"
          placeholder="Enter your email"
          control={control}
          error={errors.email?.message ?? ''}
          required={true}
        />
        <InputField
          size="large"
          name="firstName"
          label="First Name"
          placeholder="Enter your first name"
          control={control}
          error={errors.firstName?.message ?? ''}
          required={true}
        />
        <InputField
          size="large"
          name="lastName"
          label="Last Name"
          placeholder="Enter your last name"
          control={control}
          error={errors.lastName?.message ?? ''}
          required={true}
        />

        <PhoneInputField
          label="Phone Number"
          name="phone"
          control={control}
          error={errors.phone?.message ?? ''}
        />
        <Button
          htmlType="submit"
          type="primary"
          disabled={isRegisterClientLoading}
          loading={isRegisterClientLoading}
        >
          Register Contact
        </Button>
      </form>

      {verifyExistingUserLoading && (
        <Space style={{ marginTop: '2rem' }}>
          <Spin tip="Verifying contact">
            <div style={{ width: '150px' }}></div>
          </Spin>
        </Space>
      )}
    </>
  );
};

export default CreateClient;
